import { parsePhase, parseTokenPredicate } from "./logic";
import { isValidDate } from "./date";
import type { Question } from "./contract";

/**
 * 表示条件 (phase) のラベル解決 (PRODUCT_DEV-574 項⑨)。
 * builder の読み取り表示 (LockedBranchCondition) と定義閲覧ビュー (SurveyDef) で共用。
 * 解決に失敗しても raw の設問 ID / 選択肢値 (8桁hex) をユーザに見せない。
 * 純関数のため shared-domain に置き vitest でフォールバックを固定する。
 */

export const DELETED_QUESTION_LABEL = "（削除された設問）";
export const UNTITLED_QUESTION_LABEL = "（無題の設問）";
export const DELETED_OPTION_LABEL = "（削除された選択肢）";
export const UNTITLED_OPTION_LABEL = "（無題の選択肢）";

export interface PhaseCondLabel {
  parentLabel: string;
  valueLabels: string[];
}

/** phase を条件ごとの表示ラベルに解決する。"always" は null。 */
export const describePhase = (
  phase: string,
  questions: Question[],
): PhaseCondLabel[] | null => {
  const conds = parsePhase(phase);
  if (!conds) return null;
  return conds.map((c) => {
    const parent = questions.find((q) => q.questionId === c.parentId);
    const parentLabel = !parent
      ? DELETED_QUESTION_LABEL
      : parent.label.trim() || UNTITLED_QUESTION_LABEL;
    const valueLabels = c.values.map((v) => describeToken(v, parent));
    return {
      parentLabel,
      valueLabels: valueLabels.length ? valueLabels : [DELETED_OPTION_LABEL],
    };
  });
};

const isInt = (s: string): boolean => /^-?\d+$/.test(s);

/** scale token を表示句にする: ">=5"→"5以上"、"2..8"→"2〜8"。解決不能はフォールバック。 */
const describeScaleToken = (token: string): string => {
  const p = parseTokenPredicate(token);
  if (!p) return isInt(token) ? token : DELETED_OPTION_LABEL;
  if (p.kind === "range")
    return isInt(p.lo) && isInt(p.hi) ? `${p.lo}〜${p.hi}` : DELETED_OPTION_LABEL;
  if (!isInt(p.operand)) return DELETED_OPTION_LABEL;
  const suffix = { ">=": "以上", "<=": "以下", ">": "超", "<": "未満", "=": "" };
  return `${p.operand}${suffix[p.op]}`;
};

/** date token を表示句にする: ">=2026/07/01"→"2026/07/01以降"、"a..b"→"a〜b"。 */
const describeDateToken = (token: string): string => {
  const p = parseTokenPredicate(token);
  if (!p) return isValidDate(token) ? token : DELETED_OPTION_LABEL;
  if (p.kind === "range")
    return isValidDate(p.lo) && isValidDate(p.hi)
      ? `${p.lo}〜${p.hi}`
      : DELETED_OPTION_LABEL;
  if (!isValidDate(p.operand)) return DELETED_OPTION_LABEL;
  const suffix = { ">=": "以降", "<=": "以前", ">": "より後", "<": "より前", "=": "" };
  return `${p.operand}${suffix[p.op]}`;
};

/** 1 token を親設問型に応じた表示ラベルにする (raw 値を出さずフォールバック)。 */
export const describeToken = (
  token: string,
  parent: Question | undefined,
): string => {
  if (!parent) return DELETED_OPTION_LABEL;
  if (parent.type === "radio" || parent.type === "checkbox") {
    const o = parent.options.find((x) => x.v === token);
    return o ? o.label.trim() || UNTITLED_OPTION_LABEL : DELETED_OPTION_LABEL;
  }
  if (parent.type === "scale") return describeScaleToken(token);
  if (parent.type === "date") return describeDateToken(token);
  return DELETED_OPTION_LABEL;
};

/** phase を一文にする: 「親A」が「x、y」、かつ「親B」が「z」の場合に表示。 */
export const phaseNoteText = (
  phase: string,
  questions: Question[],
): string | null => {
  const conds = describePhase(phase, questions);
  if (!conds) return null;
  return (
    conds
      .map((c) => `「${c.parentLabel}」が「${c.valueLabels.join("、")}」`)
      .join("、かつ") + "の場合に表示"
  );
};
